/**
 * Workflow Modal Component
 *
 * Generic modal for workflow configuration and execution
 * Features:
 * - Overlay with click-outside to close
 * - ESC key to close
 * - Loading state with spinner overlay
 * - Responsive design
 */

import React, { useEffect, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Workflow } from '../utils/api';

interface WorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflow: Workflow | null;
  children: React.ReactNode;
  isLoading?: boolean;
}

export const WorkflowModal: React.FC<WorkflowModalProps> = ({
  isOpen,
  onClose,
  workflow,
  children,
  isLoading = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isLoading, onClose]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!isOpen || !workflow) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 transition-opacity"
        onClick={handleOverlayClick}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div
          ref={modalRef}
          className="relative transform overflow-hidden rounded-lg bg-card border border-border text-left transition-all sm:my-8 sm:w-full sm:max-w-2xl"
        >
          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/90">
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Processing...</p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="px-4 py-5 sm:px-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h3
                  className="text-lg font-medium leading-6 text-foreground"
                  id="modal-title"
                >
                  {workflow.name || `Workflow ${workflow.id}`}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Configure and execute workflow
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                disabled={isLoading}
                className="text-muted-foreground hover:text-foreground"
              >
                <span className="sr-only">Close</span>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6">{children}</div>
        </div>
      </div>
    </div>
  );
};
